import { StyleSheet, Text, View } from 'react-native';
import { makeCardBoxStyle } from '../index';
import { formatVolume, muscleKey } from '@jackedlogs/shared';
import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import type { WorkoutTotals } from './totalsMath';

type CardProps = {
  totals: WorkoutTotals;
  locale: string;
  unit: WeightUnit;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function WorkoutTotalsCard({ totals, locale, unit, theme, t }: CardProps) {
  const styles = totalsStyles(theme);
  return (
    <View style={styles.card}>
      <Text style={styles.cardLabel}>
        {t('mobile.logger.totalsTitle', { defaultValue: 'Workout totals' })}
      </Text>
      <View style={styles.cardRow}>
        <View style={styles.cardCell}>
          <Text style={styles.cardValue}>
            {formatVolume(totals.volumeKg, unit, locale)}
          </Text>
          <Text style={styles.cardCellLabel}>
            {t('mobile.logger.totalsVolume', { defaultValue: 'Volume' })}
          </Text>
        </View>
        <View style={styles.cardCell}>
          <Text style={styles.cardValue}>{totals.sets}</Text>
          <Text style={styles.cardCellLabel}>
            {t('mobile.logger.totalsSetsShort', { defaultValue: 'Sets' })}
          </Text>
        </View>
        <View style={styles.cardCell}>
          <Text style={styles.cardValue}>{totals.reps}</Text>
          <Text style={styles.cardCellLabel}>
            {t('mobile.logger.totalsRepsShort', { defaultValue: 'Reps' })}
          </Text>
        </View>
      </View>
      {totals.byMuscle.length > 0 ? (
        <View style={styles.muscleList}>
          <Text style={styles.muscleListLabel}>
            {t('mobile.logger.totalsByMuscle', {
              defaultValue: 'Work by muscle',
            })}
          </Text>
          {totals.byMuscle.map((m) => (
            <View key={m.muscle} style={styles.muscleRow}>
              <Text style={styles.muscleName} numberOfLines={1}>
                {t(muscleKey(m.muscle))}
              </Text>
              <Text style={styles.muscleValue}>
                {formatVolume(m.volumeKg, unit, locale)}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function totalsStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: makeCardBoxStyle(theme),
    cardLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    cardRow: {
      flexDirection: 'row',
      gap: s.s4,
    },
    cardCell: {
      flex: 1,
      alignItems: 'flex-start',
    },
    cardValue: {
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    cardCellLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '600',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginTop: s.s1,
    },
    muscleList: {
      gap: s.s2,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    muscleListLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    muscleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: s.s3,
    },
    muscleName: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
      flex: 1,
      minWidth: 0,
    },
    muscleValue: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
  });
}
